var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spread = (this && this.__spread) || function () {
    for (var ar = [], i = 0; i < arguments.length; i++) ar = ar.concat(__read(arguments[i]));
    return ar;
};
import { createOutlet } from '../../outlets';
import { InitialPointerState } from './constants';
import { selectResourceResult } from './selectResourceResult';
var ResourceCacheImplementation = /** @class */ (function () {
    function ResourceCacheImplementation(context, defaultRequestPolicy, dispatch, outlet, scope) {
        this.context = context;
        this.defaultRequestPolicy = defaultRequestPolicy;
        this.dispatch = dispatch;
        this.outlet = outlet;
        this.scope = scope;
    }
    ResourceCacheImplementation.prototype.request = function (resource, options) {
        var _a;
        var _this = this;
        if (options === void 0) { options = {}; }
        var pause = options.pause, _b = options.policy, policy = _b === void 0 ? this.defaultRequestPolicy : _b, vars = options.vars;
        var request = resource.request(vars, this.context);
        var actionOptions = {
            scope: this.scope,
            request: request,
            policies: policy !== 'cacheOnly' && !pause ? [policy] : [],
        };
        var subscriptionCount = 0;
        var keepMicrotask;
        var keptPointers = [];
        var pointersToKeep = [];
        var keepPointersIfRequired = function () {
            if (subscriptionCount && !keepMicrotask) {
                keepMicrotask = Promise.resolve().then(function () {
                    if (keepMicrotask) {
                        keepMicrotask = undefined;
                        var pointers = pointersToKeep.slice(0);
                        keptPointers.push.apply(keptPointers, __spread(pointers));
                        pointersToKeep.length = 0;
                        if (pointers.length) {
                            _this.dispatch({
                                type: 'applyModifiers',
                                pointers: pointers,
                                scope: actionOptions.scope,
                                keep: 1,
                            });
                        }
                    }
                });
            }
        };
        var last;
        var pickedPointers = (_a = {},
            _a[request.root.bucket] = new Set([String(request.root.id)]),
            _a);
        var pickerSource = createOutlet({
            getCurrentValue: function () {
                // Bail if nothing in the state that we care about has changed
                var scopeState = _this.outlet.getCurrentValue().data[_this.scope] || {};
                if (last &&
                    (last.scopeState === scopeState ||
                        !havePointerStatesChanged(pickedPointers, scopeState, last.scopeState))) {
                    return last.picker;
                }
                var pickPointer = function (pointer) {
                    var bucket = pointer.bucket;
                    var id = String(pointer.id);
                    // Prevent the store from purging anything we care about while there's
                    // an active subscription.
                    var bucketIds = pickedPointers[pointer.bucket];
                    if (!bucketIds) {
                        bucketIds = pickedPointers[pointer.bucket] = new Set();
                    }
                    if (!bucketIds.has(id)) {
                        bucketIds.add(id);
                        pointersToKeep.push(pointer);
                        keepPointersIfRequired();
                    }
                    var state = (scopeState[bucket] &&
                        scopeState[bucket][String(id)]) || __assign(__assign({}, InitialPointerState), { pointer: pointer });
                    var value = state.value;
                    return {
                        data: value && value.type === 'data' ? value.data : undefined,
                        hasData: !!(value && value.type === 'data'),
                        hasRejection: !!(value && value.type === 'rejection'),
                        invalidated: !!state.invalidated,
                        pending: isPending(state, policy !== 'cacheOnly'),
                        primed: isPrimed(state, policy !== 'cacheOnly', !!pause),
                        rejection: value && value.type === 'rejection' ? value.rejection : undefined,
                    };
                };
                last = {
                    scopeState: scopeState,
                    picker: function (pointerOrList) {
                        return (Array.isArray(pointerOrList)
                            ? pointerOrList.map(pickPointer)
                            : pickPointer(pointerOrList));
                    },
                };
                return last.picker;
            },
            hasCurrentValue: this.outlet.hasCurrentValue,
            subscribe: function (callback) {
                subscriptionCount++;
                if (subscriptionCount === 1) {
                    _this.dispatch(__assign({ type: 'registerRequest' }, actionOptions));
                    keepPointersIfRequired();
                }
                var unsubscribe = _this.outlet.subscribe(callback);
                var unsubscribed = false;
                return function () {
                    if (!unsubscribed) {
                        unsubscribed = true;
                        subscriptionCount--;
                        unsubscribe();
                        if (subscriptionCount === 0) {
                            var unkeepPointers = keptPointers.slice(0);
                            keepMicrotask = undefined;
                            keptPointers = [];
                            pointersToKeep.length = 0;
                            _this.dispatch(__assign({ type: 'dropRequest' }, actionOptions));
                            if (unkeepPointers.length) {
                                _this.dispatch({
                                    type: 'applyModifiers',
                                    pointers: unkeepPointers,
                                    scope: actionOptions.scope,
                                    keep: -1,
                                });
                            }
                        }
                    }
                };
            },
        });
        var resultSource = selectResourceResult(request.select(pickerSource), request.root, vars);
        var buildChunksFromInput = function (input) {
            if (typeof resource === 'function') {
                return resource(vars).chunk(input).chunks;
            }
            else {
                return [
                    __assign(__assign({}, request.root), { payload: {
                            type: 'data',
                            data: input,
                        } }),
                ];
            }
        };
        var source = Object.assign(resultSource, {
            getData: function () { return resultSource.map(function (result) { return result.data; }).getValue(); },
        });
        var controller = new ResourceRequestControllerImplementation(this.dispatch, this.scope, request, this.outlet, buildChunksFromInput);
        return [source, controller];
    };
    return ResourceCacheImplementation;
}());
export { ResourceCacheImplementation };
var ResourceRequestControllerImplementation = /** @class */ (function () {
    function ResourceRequestControllerImplementation(rawDispatch, scope, request, outlet, buildChunksFromInput) {
        this.rawDispatch = rawDispatch;
        this.scope = scope;
        this.request = request;
        this.outlet = outlet;
        this.buildChunksFromInput = buildChunksFromInput;
    }
    ResourceRequestControllerImplementation.prototype.forceLoad = function () {
        var _this = this;
        // HACK ALERT. Even if multiple tasks are created, we know that task.nextId
        // will be the id of the manualLoad task, as the manualLoad reducer will
        // always create the manualLoad task before any other tasks.
        var taskId = String(this.outlet.getCurrentValue().tasks.nextId);
        var aborted = false;
        this.dispatch('manualLoad', { request: this.request });
        return function () {
            if (!aborted) {
                aborted = true;
                _this.dispatch('abandonTask', { taskId: taskId });
            }
        };
    };
    ResourceRequestControllerImplementation.prototype.invalidate = function () {
        this.dispatch('invalidate', { taskId: null });
    };
    ResourceRequestControllerImplementation.prototype.keep = function () {
        return this.applyModifier('keep');
    };
    ResourceRequestControllerImplementation.prototype.pause = function (forExternalUpdate) {
        if (forExternalUpdate === void 0) { forExternalUpdate = false; }
        return this.applyModifier(forExternalUpdate ? 'pending' : 'pause');
    };
    ResourceRequestControllerImplementation.prototype.receive = function (input) {
        this.dispatch('updateValue', {
            taskId: null,
            timestamp: Date.now(),
            chunks: this.buildChunksFromInput(input),
        });
    };
    ResourceRequestControllerImplementation.prototype.receiveRejection = function (rejection) {
        this.dispatch('updateValue', {
            taskId: null,
            timestamp: Date.now(),
            chunks: [
                __assign(__assign({}, this.request.root), { payload: {
                        type: 'rejection',
                        rejection: rejection,
                    } }),
            ],
        });
    };
    ResourceRequestControllerImplementation.prototype.applyModifier = function (modifier) {
        var _a;
        var _this = this;
        var released = false;
        this.dispatch('applyModifiers', (_a = {},
            _a[modifier] = 1,
            _a));
        return function () {
            var _a;
            if (!released) {
                released = true;
                _this.dispatch('applyModifiers', (_a = {},
                    _a[modifier] = -1,
                    _a));
            }
        };
    };
    ResourceRequestControllerImplementation.prototype.dispatch = function (type, options) {
        this.rawDispatch(__assign(__assign({}, options), { type: type, pointers: [this.request.root], scope: this.scope }));
    };
    return ResourceRequestControllerImplementation;
}());
export { ResourceRequestControllerImplementation };
function havePointerStatesChanged(pointers, x, y) {
    return Object.keys(pointers).some(function (bucket) {
        return Array.from(pointers[bucket].values()).some(function (id) { return (x[bucket] && x[bucket][id]) !== (y[bucket] && y[bucket][id]); });
    });
}
function isPending(state, hasRequestPolicy) {
    return !!(state.tasks.manualLoad ||
        state.tasks.load ||
        state.modifiers.pending ||
        // If there's no data but we can add a default request policy, then we'll
        // treat the resource as pending too.
        (hasRequestPolicy &&
            state.value === null &&
            state.tasks.load === null &&
            state.tasks.subscribe === null));
}
function isPrimed(state, hasRequestPolicy, isPaused) {
    return !(state.value === null &&
        (isPending(state, hasRequestPolicy) || state.modifiers.pause || isPaused));
}
//# sourceMappingURL=cacheImplementation.js.map